import type { SerializedError } from '@reduxjs/toolkit'
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query'

import { z } from 'zod'

export const MARKET_CLOSED_ERROR_MESSAGE =
  'The market is currently closed for one or more instruments. Please try again during market hours.'

// public-api rejects order and RFQ entry outside the instrument's market hours with this error_type.
const MarketClosedErrorResponse = z.object({
  data: z.object({ error_type: z.literal('closed') }),
})

export function isMarketClosedError(error: unknown): boolean {
  return MarketClosedErrorResponse.safeParse(error).success
}

/**
 * Parses RTK Query fetch errors and returns a human-readable error message
 *
 * RTK Query returns two different error types depending on the failure mode:
 *
 * - FetchBaseQueryError: When the request reaches the server but returns an error response
 *   (e.g., 400 Bad Request, 401 Unauthorized, 500 Internal Server Error)
 *   Contains: { status: number, data: any }
 *
 * - SerializedError: When the request fails before reaching the server or during processing
 *   (e.g., network issues, CORS errors, timeouts, parsing errors)
 *   Contains: { message: string, name: string, stack?: string }
 *
 * @param error - The error from an RTK Query mutation or query
 * @param fallbackMessage - Optional custom fallback message (defaults to 'An error occurred')
 * @returns A formatted error message string
 */
export function parseFetchError(
  error: FetchBaseQueryError | SerializedError,
  fallbackMessage = 'An error occurred',
): string {
  // Handle FetchBaseQueryError (API errors)
  if ('status' in error) {
    if (isMarketClosedError(error)) {
      return MARKET_CLOSED_ERROR_MESSAGE
    }

    // Check if error has nested error message in data
    if (error.data && typeof error.data === 'object' && 'error' in error.data) {
      // biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
      return (error.data as any).error
    }

    // Check if error has a string data (raw error message from server)
    if (error.data && typeof error.data === 'string') {
      return error.data
    }

    return `${fallbackMessage} (${error.status})`
  }

  // Handle SerializedError (network/other errors)
  if ('message' in error && error.message) {
    return error.message
  }

  return fallbackMessage
}

/**
 * User-facing message for a rejected mutation. `.unwrap()` rejects with an RTK
 * Query error, but the surrounding try often also covers request building, so
 * the value arrives as `unknown`.
 */
export function mutationErrorMessage(error: unknown, fallback: string): string {
  if (error != null && typeof error === 'object' && 'status' in error) {
    return parseFetchError(
      error as FetchBaseQueryError | SerializedError,
      fallback,
    )
  }

  if (error instanceof Error && error.message.length > 0) {
    return error.message
  }

  return fallback
}
